// packages/web/src/App.tsx
import React, { useState, useEffect } from 'react';
import ContactList from './components/contacts/ContactList';
import GroupManager from './components/groups/GroupManager';
import TodoList from './components/todos/TodoList';
import ActiveReminders from './components/reminders/ActiveReminders';
import Calendar from './components/calendar/Calendar';
import UserSettings from './components/user/UserSettings';
import EnhancedAIAssistant from './components/ai/EnhancedAIAssistant';
import PrivacyPolicy from './components/PrivacyPolicy';
import LandingPage from './components/LandingPage';
import Pricing from './components/Pricing';
import BillingSettings from './components/subscription/BillingSettings';
import SubscriptionBadge from './components/subscription/SubscriptionBadge';
import UpgradeModal from './components/subscription/UpgradeModal';
import {
  SubscriptionProvider,
  useSubscription,
} from './contexts/SubscriptionContext';
import {
  Users,
  UserCircle,
  User as UserIcon,
  CheckSquare,
  X,
  Settings,
  LogOut,
  Bell,
  Calendar as CalendarIcon,
  CreditCard,
} from 'lucide-react';
import { Contact, User } from './types';
import { api } from './services/api';
import AdminRoutes from './routes/AdminRoutes';
import useAdminAuth from './hooks/useAdminAuth';
import { subscriptionErrorEmitter, SubscriptionErrorEvent } from './utils/subscriptionInterceptor';
import LimitErrorHandler from './components/subscription/LimitErrorHandler';
import ChatWidget from './components/ChatWidget';

function App() {
  const [activeTab, setActiveTab] = useState<
    'contacts' | 'groups' | 'todos' | 'reminders' | 'calendar' | 'settings'
  >('contacts');
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isBillingOpen, setIsBillingOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const { isAdmin, isLoading: isAdminLoading } = useAdminAuth(user);
  // Add state for subscription errors
  const [subscriptionError, setSubscriptionError] = useState<SubscriptionErrorEvent | null>(null);

  // Listen for subscription errors
  useEffect(() => {
    const unsubscribe = subscriptionErrorEmitter.addListener((error) => {
      setSubscriptionError(error);
      // Could also show a toast notification here
    });
    
    return () => unsubscribe();
  }, []);

  // Check if we should show a special page
  const isPricingPath = window.location.pathname === '/pricing';
  const isPrivacyPolicyPath = window.location.pathname === '/privacy-policy';

  // Fetch notifications when user is loaded
  useEffect(() => {
    if (user) {
      fetchNotifications();

      // Set up a polling interval to fetch notifications every 2 minutes
      const notificationInterval = setInterval(fetchNotifications, 2 * 60 * 1000);

      return () => {
        clearInterval(notificationInterval);
      };
    }
  }, [user]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isNotificationsOpen) {
        const notificationElement = document.getElementById(
          'notifications-dropdown'
        );
        const notificationButton =
          document.getElementById('notifications-button');

        if (notificationElement && notificationButton) {
          if (
            !notificationElement.contains(event.target as Node) &&
            !notificationButton.contains(event.target as Node)
          ) {
            setIsNotificationsOpen(false);
          }
        }
      }
      // Close user menu on outside click too
      if (isMenuOpen) {
        const menuElement = document.getElementById('user-menu-dropdown');
        const menuButton = document.getElementById('user-menu-button');
         if (menuElement && menuButton) {
           if (
             !menuElement.contains(event.target as Node) &&
             !menuButton.contains(event.target as Node)
           ) {
             setIsMenuOpen(false);
           }
         }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationsOpen, isMenuOpen]); // Added isMenuOpen dependency

  useEffect(() => {
    // Skip auth check if we're viewing special pages
    if (isPrivacyPolicyPath || isPricingPath) {
      setLoading(false);
      return;
    }

    // Check for token parameter in URL
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
      // Store token in localStorage
      localStorage.setItem('authToken', token);
      // Remove token from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Check if this window was opened by our authentication process
    const authTokenFromOpener = sessionStorage.getItem('pendingAuthToken');
    if (authTokenFromOpener) {
      localStorage.setItem('authToken', authTokenFromOpener);
      sessionStorage.removeItem('pendingAuthToken');
    }

    checkAuth();
  }, [isPrivacyPolicyPath, isPricingPath]);

  // Effect to update contact statuses when app loads
  useEffect(() => {
    if (user && !isPrivacyPolicyPath && !isPricingPath) {
      // Update contact statuses based on settings
      api.updateContactStatuses().catch((err) => {
        console.error('Failed to update contact statuses:', err);
      });
    }
  }, [user, isPrivacyPolicyPath, isPricingPath]);

  const checkAuth = async () => {
    try {
      console.log('Checking authentication...');

      // Try session-based auth first (works in Chrome)
      try {
        const userData = await api.getCurrentUser();
        console.log('User data from session:', userData);
        setUser(userData);
        setLoading(false); // Set loading false here
        return;
      } catch (error) {
        console.log('Session auth failed, trying token fallbacks...');
      }

      // Try multiple storage locations for the token
      const getStoredToken = () => {
        // Check localStorage
        const localToken = localStorage.getItem('authToken');
        if (localToken) return localToken;

        // Check sessionStorage
        const sessionToken = sessionStorage.getItem('authToken');
        if (sessionToken) return sessionToken;

        // Check cookies
        const cookieMatch = document.cookie.match(/authToken=([^;]+)/);
        if (cookieMatch) return cookieMatch[1];

        return null;
      };

      const token = getStoredToken();
      console.log('Found token:', token ? 'Yes' : 'No');

      if (token) {
        try {
          const result = await api.verifyToken(token);
          if (result.success && result.user) {
            console.log('User data from token:', result.user);
            setUser(result.user);
            setLoading(false); // Set loading false here
            return;
          }
        } catch (err) {
          console.error('Token verification failed:', err);
          // Potentially remove invalid token here
          // localStorage.removeItem('authToken');
          // sessionStorage.removeItem('authToken');
          // document.cookie = "authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        }
      }

      // If we get here, auth failed
      console.log('All authentication methods failed');
      setUser(null);
    } catch (error) {
      console.error('Authentication failed:', error);
      setUser(null);
    } finally {
      // Ensure loading is set to false even if errors occurred early
      if (loading) {
        setLoading(false);
      }
    }
  };

  const handleLogin = () => {
    api.loginWithGoogle();
  };

  const fetchNotifications = async () => {
    try {
      const data = await api.getNotifications();
      setNotifications(data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleMarkNotificationAsRead = async (id: string) => {
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    try {
      await api.markNotificationAsRead(id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      // Revert if API call fails (optional)
      fetchNotifications(); // Or revert the specific notification
    }
  };

  const handleMarkAllAsRead = async () => {
    // Optimistic update
     const previousNotifications = [...notifications];
     setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await api.markAllNotificationsAsRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      // Revert if API call fails
      setNotifications(previousNotifications);
    }
  };

  // If viewing the pricing page, show it without any authentication checks
  if (isPricingPath) {
    return <Pricing />;
  }
  // If viewing the privacy policy, show it without any authentication checks
  if (isPrivacyPolicyPath) {
    return <PrivacyPolicy />;
  }

  if (window.location.pathname === '/admin') {
    if (loading || isAdminLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-blue-600"></div>
        </div>
      );
    }
    
    return <AdminRoutes isAdmin={isAdmin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  // If user is not logged in, show the landing page
  if (!user) {
    return <LandingPage onLogin={handleLogin} />;
  }

  // User is logged in, show the main application
  return (
    <SubscriptionProvider>
      <ChatWidget />
      <AppContent
        user={user}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        notifications={notifications}
        isNotificationsOpen={isNotificationsOpen}
        setIsNotificationsOpen={setIsNotificationsOpen}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        isSettingsOpen={isSettingsOpen}
        setIsSettingsOpen={setIsSettingsOpen}
        isBillingOpen={isBillingOpen}
        setIsBillingOpen={setIsBillingOpen}
        handleMarkNotificationAsRead={handleMarkNotificationAsRead}
        handleMarkAllAsRead={handleMarkAllAsRead}
        isAdmin={isAdmin}
        subscriptionError={subscriptionError}
        setSubscriptionError={setSubscriptionError}
      />
    </SubscriptionProvider>
  );
}

// Extract the main content to use the subscription context
interface AppContentProps {
  user: User | null;
  activeTab: string;
  setActiveTab: (
    tab: 'contacts' | 'groups' | 'todos' | 'reminders' | 'calendar' | 'settings'
  ) => void; // More specific type
  notifications: any[]; // Consider defining a Notification type
  isNotificationsOpen: boolean;
  setIsNotificationsOpen: (open: boolean) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  isSettingsOpen: boolean;
  setIsSettingsOpen: (open: boolean) => void;
  isBillingOpen: boolean;
  setIsBillingOpen: (open: boolean) => void;
  handleMarkNotificationAsRead: (id: string) => void;
  handleMarkAllAsRead: () => void;
  isAdmin: boolean;
  subscriptionError: SubscriptionErrorEvent | null;
  setSubscriptionError: (error: SubscriptionErrorEvent | null) => void;
}

const AppContent: React.FC<AppContentProps> = ({
  user,
  activeTab,
  setActiveTab,
  notifications,
  isNotificationsOpen,
  setIsNotificationsOpen,
  isMenuOpen,
  setIsMenuOpen,
  isSettingsOpen,
  setIsSettingsOpen,
  isBillingOpen,
  setIsBillingOpen,
  handleMarkNotificationAsRead,
  handleMarkAllAsRead,
  isAdmin,
  subscriptionError,
  setSubscriptionError,
}) => {
  const { subscription, usageStats, refresh } = useSubscription();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <nav className="bg-white shadow-sm z-50 sticky top-0 left-0 right-0 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-3">
          <div className="flex items-center justify-between h-12">
            {/* App title - can be hidden on very small screens if needed */}
            <h1 className="text-xl font-semibold text-gray-800 hidden xs:block flex-shrink-0">
              Inner Circle
            </h1>

            {/* Navigation tabs - optimized for mobile */}
            <div className="flex justify-center flex-1 sm:justify-start sm:flex-none px-2 sm:px-0">
              <div className="flex space-x-1 sm:space-x-2">
                {/* Contacts Button */}
                <button
                  onClick={() => setActiveTab('contacts')}
                  className={`flex items-center px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    activeTab === 'contacts'
                      ? 'bg-tag-blue-bg text-brand-primary' // Active style
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300' // Inactive style
                  }`}
                  aria-current={activeTab === 'contacts' ? 'page' : undefined}
                >
                  <UserCircle size={20} className="flex-shrink-0" />
                  <span className="hidden sm:inline ml-1.5">Contacts</span>
                </button>
                {/* Groups Button */}
                <button
                  onClick={() => setActiveTab('groups')}
                  className={`flex items-center px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    activeTab === 'groups'
                      ? 'bg-tag-blue-bg text-brand-primary' // Active style
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300' // Inactive style
                  }`}
                   aria-current={activeTab === 'groups' ? 'page' : undefined}
                >
                  <Users size={20} className="flex-shrink-0" />
                  <span className="hidden sm:inline ml-1.5">Groups</span>
                </button>
                {/* TODO Button */}
                <button
                  onClick={() => setActiveTab('todos')}
                  className={`flex items-center px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    activeTab === 'todos'
                      ? 'bg-tag-blue-bg text-brand-primary' // Active style
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300' // Inactive style
                  }`}
                   aria-current={activeTab === 'todos' ? 'page' : undefined}
                >
                  <CheckSquare size={20} className="flex-shrink-0" />
                  <span className="hidden sm:inline ml-1.5">TODO</span>
                </button>
                {/* Reminders Button */}
                <button
                  onClick={() => setActiveTab('reminders')}
                  className={`flex items-center px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    activeTab === 'reminders'
                      ? 'bg-tag-blue-bg text-brand-primary' // Active style
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300' // Inactive style
                  }`}
                   aria-current={activeTab === 'reminders' ? 'page' : undefined}
                >
                  <Bell size={20} className="flex-shrink-0" />
                  <span className="hidden sm:inline ml-1.5">Reminders</span>
                </button>
                {/* Calendar Button */}
                <button
                  onClick={() => setActiveTab('calendar')}
                  className={`flex items-center px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    activeTab === 'calendar'
                      ? 'bg-tag-blue-bg text-brand-primary' // Active style
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300' // Inactive style
                  }`}
                   aria-current={activeTab === 'calendar' ? 'page' : undefined}
                >
                  <CalendarIcon size={20} className="flex-shrink-0" />
                  <span className="hidden sm:inline ml-1.5">Calendar</span>
                </button>
              </div>
            </div>

            {/* Right side icons */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Notifications */}
              <div className="relative">
                <button
                  id="notifications-button"
                  onClick={() => {
                    setIsNotificationsOpen(!isNotificationsOpen);
                    setIsMenuOpen(false); // Close other menus
                  }}
                  className="flex items-center p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full relative"
                  aria-label="View Notifications"
                  aria-haspopup="true"
                  aria-expanded={isNotificationsOpen}
                >
                  <Bell size={22} />
                  {notifications.filter((n) => !n.read).length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-white">
                      {notifications.filter((n) => !n.read).length > 9 ? '9+' : notifications.filter((n) => !n.read).length}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {isNotificationsOpen && (
                  <div
                    id="notifications-dropdown"
                    className="origin-top-right absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none py-1 z-50"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="notifications-button"
                  >
                    <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-800">Notifications</h3>
                      {notifications.filter((n) => !n.read).length > 0 && (
                        <button
                          onClick={(e) => {
                              e.stopPropagation(); // Prevent dropdown close
                              handleMarkAllAsRead();
                          }}
                          className="text-xs font-medium text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="text-gray-500 text-center text-sm py-4 px-4">
                          You're all caught up!
                        </p>
                      ) : (
                        notifications.map((notification) => (
                          <button
                            key={notification.id}
                            className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 block ${
                              !notification.read ? 'bg-blue-50' : ''
                            }`}
                            onClick={(e) => {
                                e.stopPropagation(); // Prevent dropdown close if needed, but should close on action
                                handleMarkNotificationAsRead(notification.id);
                                // Optionally close dropdown after click: setIsNotificationsOpen(false);
                            }}
                            role="menuitem"
                          >
                            <div className="flex items-start space-x-3">
                              <div
                                className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 ${
                                  !notification.read
                                    ? notification.type === 'todo'
                                      ? 'bg-orange-500'
                                      : notification.type === 'birthday'
                                        ? 'bg-purple-500'
                                        : notification.type ===
                                            'contact_status'
                                          ? 'bg-red-500'
                                          : 'bg-blue-500'
                                    : 'bg-gray-300'
                                }`}
                              ></div>
                              <div className="flex-1">
                                <p
                                  className={`text-sm ${
                                    !notification.read
                                      ? 'font-semibold text-gray-900'
                                      : 'text-gray-700'
                                  }`}
                                >
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(
                                    notification.createdAt
                                  ).toLocaleString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true,
                                  })}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* User avatar and dropdown */}
              <div className="relative">
                 {/* Subscription Badge - displayed before avatar on larger screens */}
                {subscription && (
                  <div className="mr-2 hidden lg:block">
                    <SubscriptionBadge
                      plan={subscription.planType}
                      status={subscription.status}
                      isExpiringSoon={subscription.isExpiringSoon}
                      daysRemaining={subscription.daysUntilRenewal}
                      showLabel={true}
                      size="sm"
                    />
                  </div>
                )}

                <button
                  id="user-menu-button"
                  onClick={() => {
                    setIsMenuOpen(!isMenuOpen);
                    setIsNotificationsOpen(false); // Close other menus
                  }}
                  className="flex items-center hover:bg-gray-100 rounded-full p-1"
                  aria-label="User Menu"
                  aria-haspopup="true"
                  aria-expanded={isMenuOpen}
                >
                  {user?.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.name || 'User Avatar'}
                      className="w-8 h-8 rounded-full object-cover"
                      onError={(e) => (e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'U')}&background=random`)}
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                  {/* Hide name on mobile for space */}
                  <span className="font-medium hidden sm:inline ml-2 text-sm text-gray-700">{user?.name}</span>
                </button>

                {isMenuOpen && (
                  <div
                    id="user-menu-dropdown"
                    className="origin-top-right absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none py-1 z-50"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="user-menu-button"
                  >
                    <div className="px-4 py-3 border-b border-gray-200">
                      <div className="flex items-center gap-3">
                        {user?.avatarUrl ? (
                          <img
                            src={user.avatarUrl}
                            alt={user.name || 'User Avatar'}
                            className="w-10 h-10 rounded-full object-cover"
                            onError={(e) => (e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'U')}&background=random`)}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg font-semibold">
                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                           <p className="font-semibold text-sm text-gray-900 truncate">{user?.name}</p>
                           <p className="text-xs text-gray-500 truncate">{user?.email}</p>

                           {/* Show subscription badge in menu too */}
                           {subscription && (
                             <div className="mt-1.5">
                               <SubscriptionBadge
                                 plan={subscription.planType}
                                 status={subscription.status}
                                 isExpiringSoon={subscription.isExpiringSoon}
                                 daysRemaining={subscription.daysUntilRenewal}
                                 showLabel={true}
                                 size="sm"
                               />
                             </div>
                           )}
                         </div>
                       </div>
                     </div>

                     {/* Menu Items */}
                     <div role="none">
                       {isAdmin && (
                         <button
                           onClick={() => {
                             setIsMenuOpen(false);
                             window.location.href = '/admin';
                           }}
                           className="w-full px-4 py-2 text-sm text-left text-purple-600 hover:bg-purple-50 hover:text-purple-700 flex items-center gap-2.5"
                           role="menuitem"
                         >
                           <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-shield"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                           Admin Dashboard
                         </button>
                       )}
                       <button
                         onClick={() => {
                           setIsMenuOpen(false);
                           setIsBillingOpen(true);
                         }}
                         className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center gap-2.5"
                         role="menuitem"
                       >
                         <CreditCard size={16} className="text-gray-500" />
                         Billing & Subscription
                       </button>
                       <button
                         onClick={() => {
                           setIsMenuOpen(false);
                           setIsSettingsOpen(true);
                         }}
                         className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center gap-2.5"
                         role="menuitem"
                       >
                         <Settings size={16} className="text-gray-500" />
                         Settings
                       </button>
                       <div className="border-t border-gray-100 my-1"></div>
                       <button
                         onClick={() => {
                           setIsMenuOpen(false);
                           api.logout(); // Consider redirecting after logout
                         }}
                         className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50 hover:text-red-700 flex items-center gap-2.5"
                         role="menuitem"
                       >
                         <LogOut size={16} />
                         Logout
                       </button>
                     </div>
                   </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-grow p-4 md:p-6 lg:p-8 max-w-7xl mx-auto w-full">
        {activeTab === 'contacts' ? (
          <ContactList />
        ) : activeTab === 'groups' ? (
          <GroupManager
            contacts={[]} // Component fetches its own data
            onUpdateContactGroups={() => {}} // Handled internally
          />
        ) : activeTab === 'todos' ? (
          <TodoList
            contacts={[]} // Component fetches its own data
            onUpdateContact={() => {}} // Handled internally
          />
        ) : activeTab === 'reminders' ? (
          <ActiveReminders />
        ) : activeTab === 'calendar' ? (
          <Calendar />
        ) : null}
      </main>

      {/* Add subscription error handler */}
      {subscriptionError && (
        <LimitErrorHandler
          error={subscriptionError}
          onClose={() => setSubscriptionError(null)}
          className="fixed bottom-4 right-4 z-50 max-w-md shadow-lg"
        />
      )}

      {/* Simple footer with privacy policy */}
      <footer className="w-full p-4 text-center border-t border-gray-200 mt-8">
        <a
          href="/privacy-policy"
          className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
          target="_self"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
         <span className="text-sm text-gray-400 mx-2">|</span>
         <a href="/pricing" className="text-sm text-gray-500 hover:text-gray-700 hover:underline">Pricing</a>
         <p className="text-xs text-gray-400 mt-2">© {new Date().getFullYear()} Inner Circle. All rights reserved.</p>
      </footer>

      {/* User Settings Modal */}
      {isSettingsOpen && (
        <UserSettings onClose={() => setIsSettingsOpen(false)} />
      )}

      {/* Billing Settings Modal */}
      {isBillingOpen && (
        <BillingSettings
          onClose={() => setIsBillingOpen(false)}
          subscription={subscription}
          usageStats={usageStats}
          onUpdate={refresh}
        />
      )}

      {/* Upgrade Modal (Likely triggered from elsewhere, e.g., BillingSettings or context) */}
      <UpgradeModal />

      {/* Enhanced AI Assistant */}
      {user && (
        <EnhancedAIAssistant
          user={user}
          onCreateContact={api.createContact}
          onUpdateContact={api.updateContact}
          onDeleteContact={async (id) => {
            await api.deleteContact(id);
          }}
          onRefreshContacts={api.getContacts}
          onNavigate={(route) => {
            if (route === 'settings') {
               setIsSettingsOpen(true);
               setIsBillingOpen(false);
               setIsMenuOpen(false);
               setIsNotificationsOpen(false);
            } else if (['contacts', 'groups', 'todos', 'reminders', 'calendar'].includes(route)) {
               setActiveTab(route as 'contacts' | 'groups' | 'todos' | 'reminders' | 'calendar');
            } else {
               console.warn("AI Assistant tried to navigate to unknown route:", route);
            }
          }}
        />
      )}
    </div>
  );
};

export default App;